import { useState, useEffect, useRef } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Printer, Search, Tag, Grid3X3 } from "lucide-react";
import { DateInput } from "../components/ui/date-input";

const LABEL_LAYOUTS = [
  { id: "1x2", label: "1x2 (2 ετικέτες)", cols: 1, rows: 2 },
  { id: "3x8", label: "3x8 (24 ετικέτες)", cols: 3, rows: 8 },
  { id: "2x7", label: "2x7 (14 ετικέτες)", cols: 2, rows: 7 },
  { id: "3x10", label: "3x10 (30 ετικέτες)", cols: 3, rows: 10 },
];

const Printouts = () => {
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [courtType, setCourtType] = useState("");
  const [hearingDate, setHearingDate] = useState("");
  const [layout, setLayout] = useState("3x8");
  const [startPosition, setStartPosition] = useState(1);
  const [loading, setLoading] = useState(false);
  const [qrCodes, setQrCodes] = useState({});
  const printRef = useRef(null);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (courtType) params.append("court_type", courtType);
      if (hearingDate) params.append("hearing_date", hearingDate);
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/printouts/cases?${params.toString()}`);
      setCases(res.data);
    } catch {
      toast.error("Σφάλμα φόρτωσης υποθέσεων");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCases(); }, [courtType, hearingDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCases();
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    if (selected.length === cases.length) {
      setSelected([]);
    } else {
      setSelected(cases.map(c => c.id));
    }
  };

  const selectedCases = cases.filter(c => selected.includes(c.id));
  const currentLayout = LABEL_LAYOUTS.find(l => l.id === layout) || LABEL_LAYOUTS[0];
  const totalSlots = currentLayout.cols * currentLayout.rows;

  // Παραγωγή QR codes για το live preview ανά αλλαγή επιλογής
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const baseUrl = window.location.origin;
      const cache = {};
      await Promise.all(
        selectedCases.map(async (c) => {
          try {
            cache[c.id] = await QRCode.toDataURL(`${baseUrl}/cases/${c.id}`, {
              margin: 0,
              width: 80,
              errorCorrectionLevel: "M",
            });
          } catch {
            cache[c.id] = null;
          }
        })
      );
      if (!cancelled) setQrCodes(cache);
    })();
    return () => { cancelled = true; };
  }, [selected, cases]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = async () => {
    if (selectedCases.length === 0) {
      toast.error("Επιλέξτε τουλάχιστον μία υπόθεση");
      return;
    }
    // Pre-generate QR codes for each case (data URLs)
    const baseUrl = window.location.origin;
    const qrCache = {};
    await Promise.all(
      selectedCases.map(async (c) => {
        try {
          qrCache[c.id] = await QRCode.toDataURL(`${baseUrl}/cases/${c.id}`, {
            margin: 0,
            width: 100,
            errorCorrectionLevel: "M",
          });
        } catch {
          qrCache[c.id] = null;
        }
      })
    );
    const printWindow = window.open("", "_blank");
    const labelsHtml = buildLabelsHtml(selectedCases, currentLayout, startPosition, qrCache);
    printWindow.document.write(labelsHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 800);
  };

  return (
    <div className="space-y-6" data-testid="printouts-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Εκτυπώσεις Ετικετών</h1>
        <Button onClick={handlePrint} disabled={selected.length === 0} data-testid="print-labels-btn">
          <Printer className="w-4 h-4 mr-2" /> Εκτύπωση ({selected.length})
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
              <Input placeholder="Αναζήτηση ΓΑΚ, ΕΑΚ, Διάδικοι..." value={search}
                onChange={e => setSearch(e.target.value)} data-testid="search-input" />
              <Button type="submit" variant="outline" data-testid="search-btn">
                <Search className="w-4 h-4" />
              </Button>
            </form>
            <Select value={courtType} onValueChange={v => setCourtType(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px]" data-testid="court-type-select">
                <SelectValue placeholder="Τύπος Δικαστηρίου" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλα</SelectItem>
                <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
              </SelectContent>
            </Select>
            <DateInput value={hearingDate} onChange={e => setHearingDate(e.target.value)}
              className="w-[160px]" data-testid="hearing-date-filter" />
            <Select value={layout} onValueChange={setLayout}>
              <SelectTrigger className="w-[170px]" data-testid="layout-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LABEL_LAYOUTS.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 whitespace-nowrap">Θέση εκκίνησης:</span>
              <Input type="number" min={1} max={totalSlots} value={startPosition}
                onChange={e => setStartPosition(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-[70px]" data-testid="start-position-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview + Table side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cases Table */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="w-4 h-4" /> Υποθέσεις ({cases.length})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={selectAll} data-testid="select-all-btn">
                {selected.length === cases.length ? "Αποεπιλογή Όλων" : "Επιλογή Όλων"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-slate-400 text-sm py-4">Φόρτωση...</p>
            ) : cases.length === 0 ? (
              <p className="text-slate-400 text-sm py-4">Δεν βρέθηκαν υποθέσεις</p>
            ) : (
              <div className="max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>ΓΑΚ/ΕΑΚ</TableHead>
                      <TableHead>Τύπος</TableHead>
                      <TableHead>Αντικείμενο</TableHead>
                      <TableHead>Δικάσιμος</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cases.map(c => (
                      <TableRow key={c.id} className={selected.includes(c.id) ? "bg-blue-50" : ""}>
                        <TableCell>
                          <Checkbox checked={selected.includes(c.id)}
                            onCheckedChange={() => toggleSelect(c.id)}
                            data-testid={`select-case-${c.id}`} />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {c.gak_number}/{c.gak_year}
                          {c.eak_number && <span className="text-indigo-600 ml-1">({c.eak_number})</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{c.court_type}</Badge>
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate text-sm">{c.subject || "-"}</TableCell>
                        <TableCell className="text-sm">
                          {c.hearing_date ? new Date(c.hearing_date).toLocaleDateString("el-GR") : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Label Preview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Grid3X3 className="w-4 h-4" /> Προεπισκόπηση Ετικετών
            </CardTitle>
            <CardDescription>
              {selected.length} ετικέτες, θέση εκκίνησης: {startPosition}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div ref={printRef} className="border rounded-lg p-2 bg-white"
              style={{ display: "grid", gridTemplateColumns: `repeat(${currentLayout.cols}, 1fr)`, gap: "2px" }}
              data-testid="label-preview">
              {Array.from({ length: totalSlots }, (_, i) => {
                const pos = i + 1;
                const caseIdx = pos - startPosition;
                const caseData = caseIdx >= 0 && caseIdx < selectedCases.length ? selectedCases[caseIdx] : null;
                return (
                  <div key={i}
                    className={`border rounded text-[7px] leading-tight p-1 h-[60px] overflow-hidden flex items-center gap-1 ${
                      caseData ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-dashed border-slate-200"
                    }`}>
                    {caseData ? (
                      <>
                        <div className="flex-1 min-w-0 flex flex-col gap-[1px]">
                          <p className="truncate font-bold" style={{fontSize: "8px"}}>{(caseData.court_branch || "Πρωτοδικείο Αθηνών").toUpperCase()}</p>
                          <p className="truncate font-semibold" style={{fontSize: "6px"}}>{buildProcedureLabel(caseData.procedure_type, caseData.court_type)}</p>
                          <p className="truncate font-bold text-blue-700" style={{fontSize: "8px"}}>
                            ΕΑΚ {caseData.eak_number || "-"}/{caseData.gak_year} · ΓΑΚ {caseData.gak_number}/{caseData.gak_year}
                          </p>
                          <p className="truncate" style={{fontSize: "6px"}}>{(caseData.subject || "-").toUpperCase()}</p>
                        </div>
                        {qrCodes[caseData.id] && (
                          <img src={qrCodes[caseData.id]} alt="QR" className="w-[40px] h-[40px] flex-shrink-0" />
                        )}
                      </>
                    ) : (
                      <span className="text-slate-300">{pos}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function buildProcedureLabel(proc, courtType) {
  const p = (proc || "Νέα Τακτική").trim();
  const ct = (courtType || "").trim();
  // Μετατροπή court_type σε γενική κεφαλαία:
  // "Μονομελές" → "ΜΟΝΟΜΕΛΟΥΣ", "Πολυμελές" → "ΠΟΛΥΜΕΛΟΥΣ"
  const ctGenitive = ct
    .replace(/ές$/i, "ΟΥΣ")
    .replace(/ες$/i, "ΟΥΣ")
    .toUpperCase();
  if (!ctGenitive) return p.toUpperCase();
  // Αν το procedure ήδη περιέχει την ίδια λέξη στη γενική, μην την επαναλάβεις
  if (p.toUpperCase().includes(ctGenitive)) return p.toUpperCase();
  return `${p.toUpperCase()} ${ctGenitive}`;
}

function buildLabelsHtml(cases, layout, startPos, qrCache = {}) {
  const totalSlots = layout.cols * layout.rows;
  let labels = "";
  for (let i = 0; i < totalSlots; i++) {
    const pos = i + 1;
    const idx = pos - startPos;
    const c = idx >= 0 && idx < cases.length ? cases[idx] : null;
    const qrImg = c && qrCache[c.id]
      ? `<img class="qr" src="${qrCache[c.id]}" alt="QR" />`
      : "";
    labels += `<div class="label">${c ? `
      <div class="content">
        <div class="branch">${(c.court_branch || "Πρωτοδικείο Αθηνών").toUpperCase()}</div>
        <div class="procedure">${buildProcedureLabel(c.procedure_type, c.court_type)}</div>
        <div class="numbers">ΕΑΚ ${c.eak_number || "-"}/${c.gak_year || ""} &nbsp; · &nbsp; ΓΑΚ ${c.gak_number || ""}/${c.gak_year || ""}</div>
        <div class="subject">${(c.subject || "-").toString().toUpperCase()}</div>
      </div>
      ${qrImg}
    ` : "&nbsp;"}</div>`;
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ετικέτες Υποθέσεων</title>
<style>
  @page { margin: 5mm; }
  body { font-family: Arial, sans-serif; margin: 0; }
  .grid { display: grid; grid-template-columns: repeat(${layout.cols}, 1fr); gap: 0; width: 100%; }
  .label { border: 0.5px solid #ccc; padding: 6px 10px; overflow: hidden;
    height: calc((297mm - 10mm) / ${layout.rows}); box-sizing: border-box;
    display: flex; flex-direction: row; align-items: center; gap: 8px; }
  .content { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; gap: 2px; }
  .branch { font-size: 18pt; font-weight: 700; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .procedure { font-size: 13pt; font-weight: 600; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .numbers { font-size: 16pt; font-weight: 700; color: #1e40af; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .subject { font-size: 11pt; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .qr { width: 80px; height: 80px; flex: 0 0 auto; }
</style></head><body><div class="grid">${labels}</div></body></html>`;
}

export default Printouts;
